import {Formik} from 'formik'
import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import {Form, Modal} from 'react-bootstrap'
import {InfoCard2} from '../../widgets/components/UI/InfoCard2'
import {useIntl} from 'react-intl'
import {ToolTipUIForModal} from '../../widgets/components/UI/ToolTipUIForModal'
import TextInput from '../../widgets/components/Input/TextInput'
import TextArea from '../../widgets/components/Input/TextArea'
import folderIcon from '../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import {getFoldersLevel1AndLevel2Api} from '../../recordskeeper-ai/core/_requests'
import {useAuth} from '../../auth'
import {createRole, getRole, updateRole} from '../core/_requests'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {DateTime} from 'luxon'
import {moduleTitles, resourceTitles} from '../core/_constants'
import Skeleton from 'react-loading-skeleton'

type CheckedOperations = {
  [moduleId: string]: {
    [key: string]: boolean
  }
}

const AddTeamRoleModal = ({
  showModal,
  setShowModal,
  roleId,
  setRoleId,
  getAllCompanyRoles,
}: {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  roleId: string
  setRoleId: (id: string) => void
  getAllCompanyRoles: () => Promise<void>
}) => {
  const [selectedColor, setSelectedColor] = useState('#F56767')
  const [expandedModules, setExpandedModules] = useState<{[key: string]: boolean}>({})
  const [checkedOperations, setCheckedOperations] = useState<CheckedOperations>({})
  const {formatMessage} = useIntl()
  const [currentDocumentStep, setCurrentDocumentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false) //eslint-disable-line
  const {companyId, currentUser} = useAuth()
  const [folderStructure, setFolderStructure] = useState<any>([])
  const [expandedFolders, setExpandedFolders] = useState<{[key: string]: boolean}>({})
  const formikRef = useRef<any>(null) //eslint-disable-line
  const [checkedItems, setCheckedItems] = useState<{[key: string]: boolean}>({})
  const [selectedAccess, setSelectedAccess] = useState<{[key: string]: string}>({})
  const [roleData, setRoleData] = useState<any>({})
  const [previewRolePriviledge, setPreviewRolePriviledge] = useState<any[]>([])

  const initialValues = {
    roleName: roleData?.roleName || '',
    roleDescription: roleData?.roleDescription || '',
    tagColor: selectedColor || '#F56767',
  }
  const roleSchema = Yup.object().shape({
    roleName: Yup.string()
      .min(3, formatMessage({id: 'Minimum 3 characters'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Role name is required'})),
    roleDescription: Yup.string()
      .min(3, formatMessage({id: 'Minimum 3 characters'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Role description is required'})),
    tagColor: Yup.string().required(formatMessage({id: 'Tag Color is required'})),
  })

  // Toggle function
  const toggleExpand = (id: string, event?: React.MouseEvent) => {
    if (event) event.stopPropagation() // Prevent checkbox clicks from triggering expand
    setExpandedModules((prev) => ({
      ...prev,
      [id]: !prev[id], // Toggle state
    }))
  }

  const toggleExpandForRK = (id: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }
  // Function to check/uncheck all child operations when parent is clicked
  const handleParentCheck = (moduleId: string, operation: string) => {
    setCheckedOperations((prev) => {
      const module = modules.find((m) => m.id === moduleId)
      if (!module) return prev

      const allChecked = prev[moduleId]?.[operation] ?? false
      const updatedOperations = {...prev[moduleId]}

      // Toggle parent checkbox
      updatedOperations[operation] = !allChecked

      // Toggle all child checkboxes for this operation
      module.resources.forEach((resource) => {
        updatedOperations[`${resource.id}-${operation}`] = !allChecked
      })

      return {...prev, [moduleId]: updatedOperations}
    })
  }
  // Function to check/uncheck child and update parent state
  // const handleChildCheck = (moduleId: string, resourceId: string, operation: string) => {
  //   setCheckedOperations((prev) => {
  //     const updatedOperations = { ...(prev[moduleId] ?? {}) }
  //     const resourceKey = `${resourceId}-${operation}`

  //     // Toggle child checkbox
  //     updatedOperations[resourceKey] = !(updatedOperations[resourceKey] ?? false)

  //     const module = modules.find((m) => m.id === moduleId)
  //     if (module) {
  //       // Check if all child checkboxes for this operation are checked
  //       const allChecked = module.resources.every((r) => updatedOperations[`${r.id}-${operation}`])

  //       // Update parent checkbox based on children
  //       updatedOperations[operation] = allChecked
  //     }

  //     return { ...prev, [moduleId]: updatedOperations }
  //   })
  // }
  const handleChildCheck = (moduleId: string, resourceId: string, operation: string) => {
    setCheckedOperations((prev) => {
      const updatedOperations = {...(prev[moduleId] ?? {})}
      const resourceKey = `${resourceId}-${operation}`
      const module = modules.find((m) => m.id === moduleId)
      if (!module) return prev

      // Toggle the selected child checkbox
      updatedOperations[resourceKey] = !(updatedOperations[resourceKey] ?? false)

      // Count total and checked child checkboxes for this operation
      const total = module.resources.length
      const checkedCount = module.resources.filter(
        (r) => updatedOperations[`${r.id}-${operation}`]
      ).length

      // Determine and update parent checkbox and indeterminate state
      if (checkedCount === 0) {
        updatedOperations[operation] = false
        setCheckedItems((prevCI) => ({
          ...prevCI,
          [`${moduleId}_${operation}_indeterminate`]: false,
        }))
      } else if (checkedCount === total) {
        updatedOperations[operation] = true
        setCheckedItems((prevCI) => ({
          ...prevCI,
          [`${moduleId}_${operation}_indeterminate`]: false,
        }))
      } else {
        updatedOperations[operation] = false
        setCheckedItems((prevCI) => ({...prevCI, [`${moduleId}_${operation}_indeterminate`]: true}))
      }

      return {...prev, [moduleId]: updatedOperations}
    })
  }

  const handleFullAccessToggle = (moduleId: string) => {
    const module = modules.find((m) => m.id === moduleId)
    if (!module) return

    const allOps = ['read', 'create', 'update', 'delete']
    const shouldCheckAll = !allOps.every((op) => checkedOperations[moduleId]?.[op])

    const updatedOps: Record<string, boolean> = {}
    allOps.forEach((op) => {
      updatedOps[op] = shouldCheckAll
      module.resources.forEach((r) => {
        updatedOps[`${r.id}-${op}`] = shouldCheckAll
      })
      // Clear indeterminate
      setCheckedItems((prev) => ({
        ...prev,
        [`${moduleId}_${op}_indeterminate`]: false,
      }))
    })

    setCheckedOperations((prev) => ({
      ...prev,
      [moduleId]: updatedOps,
    }))
  }

  const transformPermissions = (checkedOperations: CheckedOperations) => {
    return Object.entries(checkedOperations).flatMap(([moduleId, operations]) =>
      Object.entries(operations)
        .filter(([, isChecked]) => isChecked) // Only keep true values
        .map(([key]) => {
          const parts = key.split('-') // Split key by '-'
          const operation = parts.pop()! // Extract last part as operation
          const resource = parts.length ? parts.join('-') : moduleId // Use moduleId if no resource part exists

          return {resource, operation}
        })
    )
  }

  const filterCheckedOperations = (checkedOperations: Record<string, Record<string, boolean>>) => {
    const filteredOperations: Record<string, Record<string, boolean>> = {}

    Object.entries(checkedOperations).forEach(([moduleId, operations]) => {
      const filteredOps = Object.entries(operations).reduce((acc, [key, value]) => {
        if (!['read', 'create', 'update', 'delete'].includes(key)) {
          acc[key] = value // Only keep submodule operations (e.g., "data-rooms-read")
        }
        return acc
      }, {} as Record<string, boolean>)

      if (Object.keys(filteredOps).length > 0) {
        filteredOperations[moduleId] = filteredOps // Only add module if it has submodule operations
      }
    })

    return filteredOperations
  }

  const handleParentCheckboxChange = (parentId: string, children: any[]) => {
    const isChecked = !checkedItems[parentId]

    setCheckedItems((prev) => {
      const updated = {...prev, [parentId]: isChecked}

      children.forEach((child) => {
        updated[child.id] = isChecked
      })

      return updated
    })

    setSelectedAccess((prev) => {
      const updated = {...prev}

      children.forEach((child) => {
        updated[child.id] = isChecked ? 'read' : ''
      })

      return updated
    })

    updateParentCheckboxState(parentId, children)
  }

  // const handleFullAccessParentChange = (parentId: string, children: any[]) => {
  //   const isFullAccessChecked = selectedAccess[parentId] === 'full'

  //   setSelectedAccess((prev) => {
  //     const updated = { ...prev, [parentId]: isFullAccessChecked ? '' : 'full' }

  //     children.forEach((child) => {
  //       updated[child.id] = isFullAccessChecked ? 'read' : 'full'
  //     })

  //     return updated
  //   })

  //   updateParentCheckboxState(parentId, children)
  // }
  const setParentAndChildrenAccess = (
    parentId: string,
    children: any[],
    accessType: 'read' | 'full'
  ) => {
    setSelectedAccess((prev) => {
      const updated = {...prev}

      // Set parent
      updated[parentId] = accessType

      // Set all children
      children.forEach((child) => {
        updated[child.id] = accessType
      })

      return updated
    })
  }

  const handleChildCheckboxChange = (childId: string, parentId: string, children: any[]) => {
    setCheckedItems((prev) => {
      const newCheckedState = !prev[childId]
      const updated = {...prev, [childId]: newCheckedState}
      return updated
    })

    setSelectedAccess((prev) => {
      const updated = {...prev, [childId]: prev[childId] ? '' : 'read'}
      return updated
    })

    updateParentCheckboxState(parentId, children)
  }

  const handleAccessChange = (
    childId: string,
    newAccess: 'read' | 'full' | '',
    parentId: string,
    siblings: any[]
  ) => {
    setSelectedAccess((prev) => {
      const updated = {...prev}

      updated[childId] = newAccess

      const siblingAccess = siblings.map((child) =>
        child.id === childId ? newAccess : updated[child.id]
      )

      const allRead = siblingAccess.every((val) => val === 'read')
      const allFull = siblingAccess.every((val) => val === 'full')

      if (allRead) {
        updated[parentId] = 'read'
      } else if (allFull) {
        updated[parentId] = 'full'
      } else {
        updated[parentId] = 'hyphen'
      }

      return updated
    })
  }

  const updateParentCheckboxState = (parentId: string, children: any[]) => {
    setCheckedItems((prev) => {
      const allChecked = children.every((child) => prev[child.id])
      const someChecked = children.some((child) => prev[child.id])

      return {
        ...prev,
        [parentId]: allChecked,
        [`${parentId}_indeterminate`]: !allChecked && someChecked,
      }
    })

    setSelectedAccess((prev) => {
      const allFullAccess = children.every((child) => prev[child.id] === 'full')
      const someFullAccess = children.some((child) => prev[child.id] === 'full')

      return {...prev, [parentId]: allFullAccess ? 'full' : someFullAccess ? 'hyphen' : ''}
    })
  }

  const modules = [
    {
      id: 'data-rooms',
      title: 'Data Rooms',
      resources: [
        {
          id: 'data-rooms',
          title: 'Data Rooms',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'digital-signing',
      title: 'Digital Signing',
      resources: [
        {
          id: 'digital-signing',
          title: 'Digital Signing',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'stakeholders-management',
      title: 'Stakeholders Management',
      resources: [
        {
          id: 'shareholders-investors',
          title: 'Shareholders & Investors',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'share-class',
          title: 'Share Class',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'board-members',
          title: 'Board Members',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'founders-promoters',
          title: 'Founders & Promoters',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'management-team',
          title: 'Management Team',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'legal-compliance',
          title: 'Legal & Compliance',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'finance-accounts',
          title: 'Finance & Accounts',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'advisors-mentors',
          title: 'Advisors & Mentors',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'compliance-meetings',
      title: 'Compliance Meeting',
      resources: [
        {
          id: 'board-meeting',
          title: 'Board Meeting',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'shareholders-meeting',
          title: 'Shareholders Meeting',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'other-meetings',
          title: 'Other Meetings',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'captable-management',
      title: 'Cap Table Management',
      resources: [
        {
          id: 'captable-management',
          title: 'Cap Table Management',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'shared-access',
      title: 'Shared Access',
      resources: [
        {
          id: 'shared-access-calendar',
          title: 'Calendar',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'shared-access-inbox',
          title: 'Inbox',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'shared-access-contacts',
          title: 'Contacts',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'team-management',
      title: 'Team Management',
      resources: [
        {
          id: 'team-management',
          title: 'Team Management',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'billing-management',
      title: 'Billing Management',
      resources: [
        {
          id: 'billing-management',
          title: 'Billing Management',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'company-settings',
      title: 'Company Settings',
      resources: [
        {
          id: 'company-settings',
          title: 'Company Settings',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
    {
      id: 'recordskeeper-settings',
      title: 'RecordsKeeper Settings',
      resources: [
        {
          id: 'classification-settings',
          title: 'Classification Settings',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'legal-hold-settings',
          title: 'Legal Hold Settings',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'archival-settings',
          title: 'Archival Settings',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'general-settings',
          title: 'General Settings',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'analytics-reports',
          title: 'Analytics & Reports',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'access-logs',
          title: 'Access Logs',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'audit-logs',
          title: 'Audit Logs',
          operations: ['read', 'create', 'update', 'delete'],
        },
        {
          id: 'ai-chat',
          title: 'AI Chat',
          operations: ['read', 'create', 'update', 'delete'],
        },
      ],
    },
  ]

  const transformObject = (permissions: Record<string, Record<string, boolean>>) => {
    return Object.entries(permissions).map(([moduleId, resources]) => {
      const transformedResources: Record<string, Set<string>> = {}

      Object.keys(resources).forEach((operation: string) => {
        const parts = operation.split('-')
        const action = parts.pop()
        const resourceId = parts.length > 0 ? parts.join('-') : moduleId

        if (!transformedResources[resourceId]) {
          transformedResources[resourceId] = new Set()
        }

        if (action) {
          transformedResources[resourceId].add(action)
        }
      })

      const resourceEntries = Object.entries(transformedResources)

      if (resourceEntries.length === 1 && resourceEntries[0][0] === moduleId) {
        return {
          id: moduleId,
          title: moduleTitles[moduleId] ?? moduleId, // ✅ Use optional chaining (safe lookup)
          operations: Array.from(resourceEntries[0][1]),
          resources: [],
        }
      }

      return {
        id: moduleId,
        title: moduleTitles[moduleId] ?? moduleId, // ✅ Safe lookup
        resources: resourceEntries.map(([id, operations]) => ({
          id,
          title: resourceTitles[id] ?? id, // ✅ Safe lookup
          operations: Array.from(operations),
        })),
      }
    })
  }

  function getFoldersWithChildren(folderObject: any) {
    let results: any[] = []

    function traverse(obj: any) {
      Object.values(obj).forEach((folder: any) => {
        if (folder.currentFolderLevel === 'LevelOne') {
          // Extract LevelOne folder and its LevelTwo children
          results.push({
            id: folder.id,
            name: folder.name,
            uuid: folder.uuid,
            type: folder.type,
            lastModified: folder.lastModified,
            activities: folder.activities,
            currentFolderLevel: folder.currentFolderLevel,
            nextFolderLevel: folder.nextFolderLevel,
            children: Object.values(folder.children || {}).filter(
              (child: any) => child.currentFolderLevel === 'LevelTwo'
            ),
          })
        }

        // Continue traversal for other LevelOne folders in the object
        if (folder.children && Object.keys(folder.children).length > 0) {
          traverse(folder.children)
        }
      })
    }

    traverse(folderObject)
    return results
  }

  async function getFoldersLevel1AndLevel2() {
    if (!companyId) return
    try {
      setIsLoading(true)

      const {
        data: {data, success},
      } = await getFoldersLevel1AndLevel2Api(companyId)

      if (success) {
        setFolderStructure(getFoldersWithChildren(data.updatedFolderStructure))
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getFoldersLevel1AndLevel2()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const colors = [
    {color: '#F56767', id: 'red'},
    {color: '#AA72E8', id: 'purple'},
    {color: '#B30B30', id: 'darkRed'},
    {color: '#FF66FF', id: 'pink'},
    {color: '#66CC66', id: 'green'},
    {color: '#FFD700', id: 'yellow'},
    {color: '#3399FF', id: 'blue'},
  ]

  const nextStep = () => {
    const formik = formikRef.current
    if (
      currentDocumentStep === 1 &&
      formik &&
      (!formik.values.roleName || !formik.values.roleDescription || !formik.isValid)
    ) {
      return
    }
    if (currentDocumentStep === 3) {
      const modulePerms = transformPermissions(filterCheckedOperations(checkedOperations))
      const rkData = generateRKData(folderStructure)

      if (modulePerms.length === 0 && rkData.length === 0) {
        return
      }
      setPreviewRolePriviledge(transformObject(checkedOperations))
    }
    if (currentDocumentStep < 4) {
      setCurrentDocumentStep((prev) => prev + 1)
    }
  }

  const prevStep = async () => {
    if (currentDocumentStep > 1) {
      setCurrentDocumentStep((prev) => prev - 1)
    }
  }

  const generateRKData = (data: any[]) => {
    const rkdata: any[] = []

    data.forEach((parent) => {
      const children = parent.children || []
      const isParentChecked = checkedItems[parent.id]
      const isParentIndeterminate = checkedItems[`${parent.id}_indeterminate`]
      const isParentFullAccess = selectedAccess[parent.id] === 'full'

      const selectedChildren = children.filter((child: any) => checkedItems[child.id])
      const allChildrenChecked = selectedChildren.length === children.length
      const allChildrenFullAccess = selectedChildren.every(
        (child: any) => selectedAccess[child.id] === 'full'
      )

      let parentAccess = 'read'
      if (allChildrenChecked && allChildrenFullAccess) {
        parentAccess = 'full'
      } else if (isParentFullAccess) {
        parentAccess = 'full'
      } else if (isParentIndeterminate) {
        parentAccess = 'read'
      }

      if (isParentChecked || isParentIndeterminate) {
        rkdata.push({
          levelId: parent.id,
          level: 1,
          access: parentAccess,
        })
      }

      selectedChildren.forEach((child: any) => {
        rkdata.push({
          levelId: child.id,
          level: 2,
          access: selectedAccess[child.id] || 'read',
        })
      })
    })

    return rkdata
  }

  async function createRoles(values: any) {
    const payload: any = {
      roleName: values.roleName,
      roleDescription: values.roleDescription,
      createdBy: currentUser?.userId,
      tagColor: values.tagColor,
      modulePermissions: transformPermissions(filterCheckedOperations(checkedOperations)),
      rkData: generateRKData(folderStructure),
    }
    if (!companyId) return
    try {
      setIsLoading(true)
      if (roleId) {
        const {
          data: {success, errors},
        } = await updateRole(companyId, roleId, payload)
        if (success) {
          toast.success('Role Updated Successfully')
          getAllCompanyRoles()
          resetData()
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
        }
      } else {
        const {
          data: {success, errors},
        } = await createRole(companyId, payload)
        if (success) {
          toast.success('Role Created Successfully')
          getAllCompanyRoles()
          resetData()
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
        }
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setShowModal(false)
      setIsLoading(false)
    }
  }

  const transformPermission = (
    modules: any[],
    modulePermissions: any[]
  ): Record<string, Record<string, boolean>> => {
    const permissions: Record<string, Record<string, boolean>> = {}

    // Update permissions based on API response
    modulePermissions.forEach(({operation, resource}: any) => {
      for (const module of modules) {
        const resourceObj = module.resources.find((r: any) => r.id === resource)
        if (resourceObj) {
          if (!permissions[module.id]) {
            permissions[module.id] = {}
          }
          permissions[module.id][operation] = true
          permissions[module.id][`${resource}-${operation}`] = true
          break
        }
      }
    })

    return permissions
  }

  const convertRKDataToCheckedItems = (rkData: {rkLevelId: string; access: string}[]) => {
    return rkData.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.rkLevelId] = item.access === 'read' || item.access === 'full'
      return acc
    }, {})
  }
  const generateSelectedAccess = (rkData: any[]) => {
    const selectedAccess: Record<string, string> = {}

    // Group rkData by parent levels
    const levelGroups: Record<string, string[]> = {}

    rkData.forEach((item) => {
      if (item.rkLevel === 2) {
        if (!levelGroups[item.parentId]) {
          levelGroups[item.parentId] = []
        }
        levelGroups[item.parentId].push(item.access)
      }
      // Store child access
      selectedAccess[item.rkLevelId] = item.access
    })

    // Determine parent access based on children
    Object.entries(levelGroups).forEach(([parentId, accessList]) => {
      const allRead = accessList.every((access) => access === 'read')
      const allFull = accessList.every((access) => access === 'full')

      if (allFull) {
        selectedAccess[parentId] = 'full'
      } else if (allRead) {
        selectedAccess[parentId] = 'read'
      } else {
        selectedAccess[parentId] = 'hyphen' // Mixed access
      }
    })

    return selectedAccess
  }

  const getCompanyRole = async () => {
    if (!companyId) return
    try {
      const {
        data: {success, data},
      } = await getRole(companyId, roleId)

      if (success) {
        setRoleData(data?.role)
        setSelectedColor(data?.role?.tagColor)
        setCheckedOperations(transformPermission(modules, data?.role?.modulePermissions || []))
        setCheckedItems(convertRKDataToCheckedItems(data?.role?.rkData))
        setSelectedAccess(generateSelectedAccess(data?.role?.rkData))
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
    }
  }

  useEffect(() => {
    if (roleId && companyId) {
      getCompanyRole()
    }
  }, [roleId, companyId]) //eslint-disable-line react-hooks/exhaustive-deps

  const resetData = () => {
    setCurrentDocumentStep(1)
    setRoleId('')
    setPreviewRolePriviledge([])
    setRoleData([])
    setSelectedAccess({})
    setCheckedItems({})
    setExpandedFolders({})
    setFolderStructure([])
    setExpandedModules({})
    setCheckedOperations({})
    // formikRef.current = null
  }
  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={createRoles}
        validationSchema={roleSchema}
        validateOnMount={true}
        enableReinitialize
        innerRef={formikRef}
      >
        {(formik) => {
          return (
            <Form>
              <Modal
                show={showModal}
                onHide={() => {
                  setShowModal(false)
                  formik.resetForm()
                  resetData()
                }}
                size='lg'
                fullscreen
              >
                <Modal.Header closeButton>
                  <h2>{formatMessage({id: 'Add New Role'})}</h2>
                </Modal.Header>
                <Modal.Body
                  className='container'
                  style={{
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <div
                    className='stepper stepper-pills stepper-column d-flex flex-column'
                    id='kt_create_account_stepper'
                  >
                    <div className='d-flex justify-content-start flex-column w-100'>
                      <div className='py-4 px-0 d-flex justify-content-start'>
                        <div
                          className='d-flex gap-6 align-items-center'
                          style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                          }}
                        >
                          {[1, 2, 3, 4].map((step) => {
                            return (
                              <div
                                key={step}
                                className={`stepper-item ${
                                  currentDocumentStep === step ? 'current' : ''
                                } custom-marign-right`}
                                style={{display: 'inline-block'}}
                              >
                                <div className='stepper-wrapper'>
                                  {step === 1 && (
                                    <div className='stepper-icon w-40px h-40px'>
                                      <i className='stepper-check'></i>
                                      <span className='stepper-number'>{step}</span>
                                    </div>
                                  )}
                                  {step === 2 && (
                                    <div className='stepper-icon w-40px h-40px'>
                                      <i className='stepper-check'></i>
                                      <span className='stepper-number'>{step}</span>
                                    </div>
                                  )}
                                  {step === 3 && (
                                    <div className='stepper-icon w-40px h-40px'>
                                      <i className='stepper-check'></i>
                                      <span className='stepper-number'>{step}</span>
                                    </div>
                                  )}
                                  {step === 4 && (
                                    <div className='stepper-icon w-40px h-40px'>
                                      <i className='stepper-check'></i>
                                      <span className='stepper-number'>{step}</span>
                                    </div>
                                  )}

                                  <div className='stepper-label'>
                                    <h3 className='stepper-title fw-semibold font-size-13'>
                                      {step === 1 && (
                                        <>
                                          {formatMessage({
                                            id: 'Step 1',
                                          })}
                                        </>
                                      )}
                                      {step === 2 && (
                                        <>
                                          {formatMessage({
                                            id: 'Step 2',
                                          })}
                                        </>
                                      )}
                                      {step === 3 && (
                                        <>
                                          {formatMessage({
                                            id: 'Step 3',
                                          })}
                                        </>
                                      )}
                                      {step === 4 && (
                                        <>
                                          {formatMessage({
                                            id: 'Step 4',
                                          })}
                                        </>
                                      )}
                                    </h3>
                                    <div className='stepper-desc font-size-12'>
                                      {step === 1 && (
                                        <>
                                          {formatMessage({
                                            id: 'Basic Details',
                                          })}
                                        </>
                                      )}
                                      {step === 2 && (
                                        <>
                                          {formatMessage({
                                            id: 'Privileges',
                                          })}
                                        </>
                                      )}
                                      {step === 3 && (
                                        <>
                                          {formatMessage({
                                            id: 'RecordsKeeper',
                                          })}
                                        </>
                                      )}
                                      {step === 4 && (
                                        <>
                                          {formatMessage({
                                            id: 'Review',
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-column justify-content-between'>
                      <div className=''>
                        {/* Step 1  */}
                        {currentDocumentStep === 1 && (
                          <>
                            <div className='w-100 my-4'>
                              <InfoCard2
                                title={formatMessage({
                                  id: 'Define a New Role for Users to Access RecordsKeeper.AI',
                                })}
                                desc={formatMessage({
                                  id: 'This wizard will guide you through defining a new role that can be assigned to users in your team, enabling them to access various modules of RecordsKeeper.AI. A role allows you to control what features and data a user can interact with. Once created, you can assign this role to multiple users as needed.',
                                })}
                                slug={`https://help.recordskeeper.ai/en/articles/10570867-how-to-add-team-members-and-configure-role-based-access-in-recordskeeper-ai`}
                                className='w-100 mb-0'
                              />
                            </div>
                            <h1 className='font-size-16 mb-4'>
                              {formatMessage({id: 'How Will You Recognize This Role?'})}
                              <ToolTipUIForModal tooltipText='The name and visual identifier you choose here will help you quickly identify this role throughout the platform when managing users, permissions, and access controls.' />
                            </h1>
                            {isLoading ? (
                              <div className='row'>
                                <div className='col-md-6 col-12'>
                                  <Skeleton
                                    height={60}
                                    width='100%'
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='col-md-6 col-12 mb-8'>
                                  <Skeleton
                                    height={60}
                                    width='100%'
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='col-md-12 col-12'>
                                  <Skeleton
                                    height={120}
                                    width='100%'
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className='row'>
                                <div className='col-md-6 col-12'>
                                  <TextInput
                                    fieldType='text'
                                    label={formatMessage({id: 'Role name'})}
                                    fieldName='roleName'
                                    formik={formik}
                                    placeholder={formatMessage({id: 'Enter Role name'})}
                                    toolTipText={formatMessage({
                                      id: `Enter a unique and descriptive name for this role (e.g., Finance Manager, Legal Team Member, Compliance Officer). This name will be used to identify the role throughout the system.`,
                                    })}
                                    isStarRequired={true}
                                    isTooltipForModal={true}
                                    onBlur={formik.handleBlur}
                                  />
                                </div>
                                <div className='col-md-6 col-12 mb-8'>
                                  <div className='d-flex'>
                                    <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0 required-black'>
                                      {formatMessage({id: 'Select Tag Color'})}
                                    </label>
                                    <ToolTipUIForModal tooltipText='Choose a distinct color to visually identify this role in the interface. Color coding helps quickly recognize different roles across the platform.' />
                                  </div>
                                  <div className='d-flex align-items-center'>
                                    {colors.map((colorObj) => (
                                      <div
                                        key={colorObj.id}
                                        onClick={() =>
                                          formik.setFieldValue('tagColor', colorObj.color)
                                        }
                                        className='me-2 rounded-circle mt-1'
                                        style={{
                                          backgroundColor: colorObj.color,
                                          width: '30px',
                                          height: '30px',
                                          border:
                                            formik.values.tagColor === colorObj.color
                                              ? '3px solid black'
                                              : 'none',
                                          cursor: 'pointer',
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div className='col-md-12 col-12'>
                                  <TextArea
                                    label={formatMessage({id: 'Role Description'})}
                                    fieldName='roleDescription'
                                    formik={formik}
                                    placeholder={formatMessage({id: 'Enter Role Description'})}
                                    toolTipText={formatMessage({
                                      id: `Provide a detailed description of this role's responsibilities and scope.Include key duties, access levels, and any specific limitations.This helps other administrators understand the role's purpose.`,
                                    })}
                                    isStarRequired
                                    isTooltipForModal={true}
                                    onBlur={formik.handleBlur}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Step 2  */}
                        {currentDocumentStep === 2 && (
                          <>
                            <div className='w-100 my-4'>
                              <InfoCard2
                                title={formatMessage({
                                  id: 'Define a New Role for Users to Access RecordsKeeper.AI',
                                })}
                                desc={formatMessage({
                                  id: 'This wizard will guide you through defining a new role that can be assigned to users in your team, enabling them to access various modules of RecordsKeeper.AI. A role allows you to control what features and data a user can interact with. Once created, you can assign this role to multiple users as needed.',
                                })}
                                slug={`https://help.recordskeeper.ai/en/articles/10570867-how-to-add-team-members-and-configure-role-based-access-in-recordskeeper-ai`}
                                className='w-100 mb-0'
                              />
                            </div>
                            <h1 className='font-size-13 mb-1'>
                              {formatMessage({id: 'Select Privileges'})}
                              <ToolTipUIForModal tooltipText='Define the specific actions this role can perform within each module.' />
                            </h1>
                            <p className='font-size-12'>
                              {formatMessage({id: 'Select Priviliges from the list below'})}
                            </p>
                            {isLoading
                              ? Array(9)
                                  .fill(null)
                                  .map((_, index) => (
                                    <Skeleton
                                      height={60}
                                      width='100%'
                                      baseColor='var(--kt-skeleton-bg-color)'
                                      highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    />
                                  ))
                              : modules.slice(0, 9).map((module: any, index: number) => {
                                  const isSingleResource =
                                    module.resources.length === 1 &&
                                    module.resources[0].id === module.id

                                  return (
                                    <div className='d-flex flex-column' key={index}>
                                      <div className='d-flex flex-column justify-content-center'>
                                        <div className='bg-white p-4 border border-2 rounded d-flex flex-column gap-4 my-2'>
                                          <div
                                            className={`d-flex flex-md-row flex-column justify-content-between align-items-center gap-4 ${
                                              module.resources.length > 0 && !isSingleResource
                                                ? 'cursor-pointer'
                                                : ''
                                            }`}
                                            onClick={() =>
                                              !isSingleResource && toggleExpand(module.id)
                                            }
                                          >
                                            <div className='d-flex justify-content-between align-items-center w-md-25 w-100'>
                                              <span className='d-flex align-items-center gap-4'>
                                                <i
                                                  className={`fa-solid fa-angle-${
                                                    expandedModules[module.id] ? 'down' : 'right'
                                                  } ps-0 ${
                                                    module.resources.length > 0 && !isSingleResource
                                                      ? ''
                                                      : 'd-none'
                                                  }`}
                                                ></i>
                                                <p className='mb-0 fw-bold'>
                                                  {formatMessage({id: module.title})}
                                                </p>
                                              </span>
                                            </div>
                                            {/* Parent Checkboxes */}
                                            <div className='d-flex justify-content-around w-100'>
                                              <div className='d-flex flex-md-row flex-column justify-content-md-around justify-content-between w-100 p-md-2 p-0'>
                                                <div className='form-check form-check-custom form-check-solid form-check-primary form-switch d-flex align-items-center mb-md-0 mb-4'>
                                                  <label className='togglebtn mx-0'>
                                                    <input
                                                      type='checkbox'
                                                      className='togglebtn__input'
                                                      checked={[
                                                        'read',
                                                        'create',
                                                        'update',
                                                        'delete',
                                                      ].every(
                                                        (op) => checkedOperations[module.id]?.[op]
                                                      )}
                                                      onChange={(e) => {
                                                        handleFullAccessToggle(module.id)
                                                        e.stopPropagation()
                                                      }}
                                                    />

                                                    <div className='togglebtn__fill'></div>
                                                  </label>
                                                  <div className='d-flex flex-column justify-content-end'>
                                                    <div className='d-flex'>
                                                      <div>
                                                        <label
                                                          className='form-check-label fw-normal font-size-13 title-notification'
                                                          data-bs-toggle='tooltip'
                                                        >
                                                          {formatMessage({id: 'Full Access'})}
                                                        </label>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                                {['read', 'create', 'update', 'delete'].map(
                                                  (operation) => (
                                                    <span
                                                      key={operation}
                                                      className='d-flex align-items-center gap-3 my-md-0 my-2'
                                                    >
                                                      <input
                                                        type='checkbox'
                                                        className='form-check-input cursor-pointer'
                                                        checked={
                                                          checkedOperations[module.id]?.[
                                                            operation
                                                          ] || false
                                                        }
                                                        ref={(el) =>
                                                          el &&
                                                          (el.indeterminate =
                                                            checkedItems[
                                                              `${module.id}_${operation}_indeterminate`
                                                            ] || false)
                                                        }
                                                        onChange={() =>
                                                          handleParentCheck(module.id, operation)
                                                        }
                                                        onClick={(e) => e.stopPropagation()}
                                                      />
                                                      <p className='mb-0 text-capitalize'>
                                                        {operation}
                                                      </p>
                                                    </span>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                          {/* Child Resources */}
                                          {expandedModules[module.id] &&
                                            module.resources.length > 0 && (
                                              <div className='border-top pt-3'>
                                                {module.resources.map(
                                                  (resource: any, resourceIndex: number) => (
                                                    <div
                                                      key={resource.id}
                                                      className={`d-flex flex-md-row flex-column align-items-center justify-content-between p-2 py-4 gap-4 ${
                                                        resourceIndex % 2
                                                          ? ''
                                                          : 'bg-primary bg-opacity-10 rounded'
                                                      }`}
                                                    >
                                                      <div className='w-md-auto w-100'>
                                                        {resource.title}
                                                      </div>
                                                      <div className='d-flex flex-md-row flex-column justify-content-around w-md-75 w-100'>
                                                        <div className='form-check form-check-custom form-check-solid form-check-primary form-switch d-flex align-items-center mb-md-0 mb-4'>
                                                          <label className='togglebtn mx-0'>
                                                            <input
                                                              type='checkbox'
                                                              className='togglebtn__input'
                                                              checked={[
                                                                'read',
                                                                'create',
                                                                'update',
                                                                'delete',
                                                              ].every(
                                                                (op) =>
                                                                  checkedOperations[module.id]?.[
                                                                    `${resource.id}-${op}`
                                                                  ]
                                                              )}
                                                              onChange={(e) => {
                                                                const shouldCheckAll = ![
                                                                  'read',
                                                                  'create',
                                                                  'update',
                                                                  'delete',
                                                                ].every(
                                                                  (op) =>
                                                                    checkedOperations[module.id]?.[
                                                                      `${resource.id}-${op}`
                                                                    ]
                                                                )

                                                                setCheckedOperations((prev) => {
                                                                  const updated = {
                                                                    ...(prev[module.id] ?? {}),
                                                                  }
                                                                  ;[
                                                                    'read',
                                                                    'create',
                                                                    'update',
                                                                    'delete',
                                                                  ].forEach((op) => {
                                                                    updated[
                                                                      `${resource.id}-${op}`
                                                                    ] = shouldCheckAll
                                                                  })
                                                                  return {
                                                                    ...prev,
                                                                    [module.id]: updated,
                                                                  }
                                                                })

                                                                e.stopPropagation()
                                                              }}
                                                            />

                                                            <div className='togglebtn__fill'></div>
                                                          </label>
                                                          <div className='d-flex flex-column justify-content-end'>
                                                            <div className='d-flex'>
                                                              <div>
                                                                <label
                                                                  className='form-check-label fw-normal font-size-13 title-notification'
                                                                  data-bs-toggle='tooltip'
                                                                >
                                                                  {formatMessage({
                                                                    id: 'Full Access',
                                                                  })}
                                                                </label>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </div>
                                                        {resource.operations.map(
                                                          (operation: any) => (
                                                            <span
                                                              key={operation}
                                                              className='d-flex align-items-center gap-md-3 gap-2 my-2'
                                                            >
                                                              <input
                                                                type='checkbox'
                                                                className='form-check-input cursor-pointer'
                                                                checked={
                                                                  checkedOperations[module.id]?.[
                                                                    `${resource.id}-${operation}`
                                                                  ] || false
                                                                }
                                                                onChange={() =>
                                                                  handleChildCheck(
                                                                    module.id,
                                                                    resource.id,
                                                                    operation
                                                                  )
                                                                }
                                                              />
                                                              <p className='mb-0 text-capitalize'>
                                                                {operation}
                                                              </p>
                                                            </span>
                                                          )
                                                        )}
                                                      </div>
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                          </>
                        )}

                        {/* Step 3  */}
                        {currentDocumentStep === 3 && (
                          <>
                            <div className='w-100 my-4'>
                              <InfoCard2
                                title={formatMessage({
                                  id: 'Define a New Role for Users to Access RecordsKeeper.AI',
                                })}
                                desc={formatMessage({
                                  id: 'This wizard will guide you through defining a new role that can be assigned to users in your team, enabling them to access various modules of RecordsKeeper.AI. A role allows you to control what features and data a user can interact with. Once created, you can assign this role to multiple users as needed.',
                                })}
                                slug={`https://help.recordskeeper.ai/en/articles/10570867-how-to-add-team-members-and-configure-role-based-access-in-recordskeeper-ai`}
                                className='w-100 mb-0'
                              />
                            </div>
                            {isLoading ? (
                              <Skeleton
                                height={60}
                                width='100%'
                                baseColor='var(--kt-skeleton-bg-color)'
                                highlightColor='var(--kt-skeleton-highlight-bg-color)'
                              />
                            ) : (
                              modules.slice(9).map((module: any, index: number) => {
                                const isSingleResource =
                                  module.resources.length === 1 &&
                                  module.resources[0].id === module.id

                                return (
                                  <div className='d-flex flex-column' key={index}>
                                    <div className='d-flex flex-column justify-content-center'>
                                      <div className='bg-white p-4 border border-2 rounded d-flex flex-column gap-4 my-2'>
                                        <div
                                          className={`d-flex flex-md-row flex-column justify-content-between align-items-center gap-4 ${
                                            module.resources.length > 0 && !isSingleResource
                                              ? 'cursor-pointer'
                                              : ''
                                          }`}
                                          onClick={() =>
                                            !isSingleResource && toggleExpand(module.id)
                                          }
                                        >
                                          <div className='d-flex justify-content-between align-items-center w-md-25 w-100'>
                                            <span className='d-flex align-items-center gap-4'>
                                              <i
                                                className={`fa-solid fa-angle-${
                                                  expandedModules[module.id] ? 'down' : 'right'
                                                } ps-0 ${
                                                  module.resources.length > 0 && !isSingleResource
                                                    ? ''
                                                    : 'd-none'
                                                }`}
                                              ></i>
                                              <p className='mb-0 fw-bold'>
                                                {formatMessage({id: module.title})}
                                              </p>
                                            </span>
                                          </div>
                                          {/* Parent Checkboxes */}
                                          <div className='d-flex justify-content-around w-100'>
                                            <div className='d-flex flex-md-row flex-column justify-content-md-around justify-content-between w-100 p-md-2 p-0'>
                                              <div className='form-check form-check-custom form-check-solid form-check-primary form-switch d-flex align-items-center mb-md-0 mb-4'>
                                                <label className='togglebtn mx-0'>
                                                  <input
                                                    type='checkbox'
                                                    className='togglebtn__input'
                                                    checked={[
                                                      'read',
                                                      'create',
                                                      'update',
                                                      'delete',
                                                    ].every(
                                                      (op) => checkedOperations[module.id]?.[op]
                                                    )}
                                                    onChange={(e) => {
                                                      handleFullAccessToggle(module.id)
                                                      e.stopPropagation()
                                                    }}
                                                  />

                                                  <div className='togglebtn__fill'></div>
                                                </label>
                                                <div className='d-flex flex-column justify-content-end'>
                                                  <div className='d-flex'>
                                                    <div>
                                                      <label
                                                        className='form-check-label fw-normal font-size-13 title-notification'
                                                        data-bs-toggle='tooltip'
                                                      >
                                                        {formatMessage({id: 'Full Access'})}
                                                      </label>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>

                                              {['read', 'create', 'update', 'delete'].map(
                                                (operation) => (
                                                  <span
                                                    key={operation}
                                                    className='d-flex align-items-center gap-3 my-md-0 my-2'
                                                  >
                                                    <input
                                                      type='checkbox'
                                                      className='form-check-input cursor-pointer'
                                                      checked={
                                                        checkedOperations[module.id]?.[operation] ||
                                                        false
                                                      }
                                                      ref={(el) => {
                                                        if (el) {
                                                          el.indeterminate =
                                                            checkedItems[
                                                              `${module.id}_${operation}_indeterminate`
                                                            ] || false
                                                        }
                                                      }}
                                                      onChange={() =>
                                                        handleParentCheck(module.id, operation)
                                                      }
                                                      onClick={(e) => e.stopPropagation()}
                                                    />

                                                    <p className='mb-0 text-capitalize'>
                                                      {operation}
                                                    </p>
                                                  </span>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        {/* Child Resources */}
                                        {expandedModules[module.id] && module.resources.length > 0 && (
                                          <div className='border-top pt-3'>
                                            {module.resources.map(
                                              (resource: any, resourceIndex: number) => (
                                                <div
                                                  key={resource.id}
                                                  className={`d-flex flex-md-row flex-column justify-content-between p-2 py-4 gap-4 ${
                                                    resourceIndex % 2
                                                      ? ''
                                                      : 'bg-primary bg-opacity-10 rounded'
                                                  }`}
                                                >
                                                  <div className='w-md-25 w-100'>
                                                    {resource.title}
                                                  </div>
                                                  <div className='d-flex flex-md-row flex-column justify-content-around w-md-75 w-100'>
                                                    <div className='form-check form-check-custom form-check-solid form-check-primary form-switch d-flex align-items-center mb-md-0 mb-4'>
                                                      <label className='togglebtn mx-0'>
                                                        <input
                                                          type='checkbox'
                                                          className='togglebtn__input'
                                                          checked={[
                                                            'read',
                                                            'create',
                                                            'update',
                                                            'delete',
                                                          ].every(
                                                            (op) =>
                                                              checkedOperations[module.id]?.[
                                                                `${resource.id}-${op}`
                                                              ]
                                                          )}
                                                          onChange={() => {
                                                            // Handle full access toggle at resource level if needed
                                                            const shouldCheckAll = ![
                                                              'read',
                                                              'create',
                                                              'update',
                                                              'delete',
                                                            ].every(
                                                              (op) =>
                                                                checkedOperations[module.id]?.[
                                                                  `${resource.id}-${op}`
                                                                ]
                                                            )

                                                            setCheckedOperations((prev) => {
                                                              const updated = {
                                                                ...(prev[module.id] ?? {}),
                                                              }
                                                              ;[
                                                                'read',
                                                                'create',
                                                                'update',
                                                                'delete',
                                                              ].forEach((op) => {
                                                                updated[`${resource.id}-${op}`] =
                                                                  shouldCheckAll
                                                              })
                                                              return {...prev, [module.id]: updated}
                                                            })
                                                          }}
                                                          onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className='togglebtn__fill'></div>
                                                      </label>
                                                      <div className='d-flex flex-column justify-content-end'>
                                                        <div className='d-flex'>
                                                          <div>
                                                            <label
                                                              className='form-check-label fw-normal font-size-13 title-notification'
                                                              data-bs-toggle='tooltip'
                                                            >
                                                              {formatMessage({id: 'Full Access'})}
                                                            </label>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>

                                                    {resource.operations.map((operation: any) => (
                                                      <span
                                                        key={operation}
                                                        className='d-flex align-items-center gap-md-3 gap-2 my-md-0 my-2'
                                                      >
                                                        <input
                                                          type='checkbox'
                                                          className='form-check-input cursor-pointer'
                                                          checked={
                                                            checkedOperations[module.id]?.[
                                                              `${resource.id}-${operation}`
                                                            ] || false
                                                          }
                                                          onChange={() =>
                                                            handleChildCheck(
                                                              module.id,
                                                              resource.id,
                                                              operation
                                                            )
                                                          }
                                                        />
                                                        <p className='mb-0 text-capitalize'>
                                                          {operation}
                                                        </p>
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })
                            )}
                            <h1 className='font-size-13 mt-2 mb-1'>
                              {formatMessage({id: 'Data Access Control on RecordsKeeper.AI'})}
                              <ToolTipUIForModal tooltipText='Set granular access permissions for departments and categories to ensure sensitive records are only accessible to authorized personnel.' />
                            </h1>
                            <p className='font-size-12'>
                              {formatMessage({
                                id: 'Select Department (Level 1) & Category (Level 2) wise roles from the list below',
                              })}
                            </p>
                            {isLoading
                              ? Array(9)
                                  .fill(null)
                                  .map((_, index) => (
                                    <Skeleton
                                      height={60}
                                      width='100%'
                                      baseColor='var(--kt-skeleton-bg-color)'
                                      highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    />
                                  ))
                              : folderStructure &&
                                folderStructure.map((data: any, index: number) => {
                                  const isExpanded = expandedFolders[data.id] || false
                                  const isParentChecked = checkedItems[data.id] || false
                                  const isFullAccessParentChecked =
                                    selectedAccess[data.id] === 'full'
                                  const isReadOnlyParentChecked = selectedAccess[data.id] === 'read'

                                  return (
                                    <div className='d-flex flex-column' key={data.id}>
                                      <div className='d-flex flex-column justify-content-center'>
                                        <div className='bg-white p-4 border border-2 rounded d-flex flex-column my-2'>
                                          <div
                                            className={`d-flex flex-md-row flex-column justify-content-between align-items-center gap-4 cursor-pointer ${
                                              isExpanded ? 'mb-4' : 'mb-0'
                                            }`}
                                            onClick={() => toggleExpandForRK(data.id)}
                                          >
                                            <div className='d-flex justify-content-start gap-4 align-items-center w-md-50 w-100'>
                                              <i
                                                className={`fa-solid fa-angle-${
                                                  isExpanded ? 'down' : 'right'
                                                } cursor-pointer p-2 pe-0`}
                                              ></i>
                                              <input
                                                type='checkbox'
                                                className='form-check-input h-20px w-20px cursor-pointer'
                                                checked={isParentChecked}
                                                ref={(el) =>
                                                  el &&
                                                  (el.indeterminate =
                                                    checkedItems[`${data.id}_indeterminate`] ||
                                                    false)
                                                }
                                                onChange={() =>
                                                  handleParentCheckboxChange(
                                                    data.id,
                                                    data.children || []
                                                  )
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                              <img src={folderIcon} alt='folder-icon' />
                                              <p className='mb-0'>
                                                {formatMessage({id: data.name})}
                                              </p>
                                            </div>
                                            <div className='d-flex gap-8 justify-content-end w-md-25 w-100'>
                                              <div className='d-flex w-100 justify-content-md-end justify-content-center gap-md-3 gap-2'>
                                                <input
                                                  type='checkbox'
                                                  className='form-check-input h-20px w-20px cursor-pointer'
                                                  checked={isReadOnlyParentChecked}
                                                  ref={(el) =>
                                                    el &&
                                                    (el.indeterminate =
                                                      selectedAccess[data.id] === 'hyphen')
                                                  }
                                                  onChange={() =>
                                                    setParentAndChildrenAccess(
                                                      data.id,
                                                      data.children || [],
                                                      'read'
                                                    )
                                                  }
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                                <p className='mb-0'>
                                                  {formatMessage({id: 'Read Only'})}
                                                </p>
                                              </div>
                                              <div
                                                className={`d-flex  w-100 justify-content-md-end justify-content-center gap-md-3 gap-2`}
                                              >
                                                <input
                                                  type='checkbox'
                                                  className='form-check-input h-20px w-20px cursor-pointer'
                                                  checked={isFullAccessParentChecked}
                                                  ref={(el) =>
                                                    el &&
                                                    (el.indeterminate =
                                                      selectedAccess[data.id] === 'hyphen')
                                                  }
                                                  onChange={() =>
                                                    setParentAndChildrenAccess(
                                                      data.id,
                                                      data.children || [],
                                                      'full'
                                                    )
                                                  }
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                                <p className='mb-0'>
                                                  {formatMessage({id: 'Full Access'})}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          <div>
                                            {isExpanded &&
                                              data.children &&
                                              data.children.map(
                                                (childrenData: any, catIndex: number) => {
                                                  const isChildChecked =
                                                    checkedItems[childrenData.id] ?? false

                                                  return (
                                                    <div
                                                      key={childrenData.id}
                                                      className={`d-flex flex-md-row flex-column align-items-center justify-content-between py-2 gap-4 department-adjuster py-4 ${
                                                        catIndex % 2
                                                          ? ''
                                                          : 'bg-primary bg-opacity-10 rounded'
                                                      }`}
                                                      // style={{ paddingLeft: '60px', paddingRight: '40px' }}
                                                    >
                                                      <div className='d-flex align-items-center gap-4 w-md-25 w-100 '>
                                                        <input
                                                          type='checkbox'
                                                          className='form-check-input h-20px w-20px cursor-pointer'
                                                          checked={isChildChecked}
                                                          onChange={() =>
                                                            handleChildCheckboxChange(
                                                              childrenData.id,
                                                              data.id,
                                                              data.children || []
                                                            )
                                                          }
                                                        />
                                                        <img src={folderIcon} alt='folder-icon' />
                                                        <p className='mb-0'>
                                                          {formatMessage({id: childrenData.name})}
                                                        </p>
                                                      </div>
                                                      <div className='w-md-75 w-100'>
                                                        <div className='d-flex justify-content-md-end justify-content-center gap-8'>
                                                          <span className='d-flex align-items-center gap-md-3 gap-2'>
                                                            <input
                                                              type='checkbox'
                                                              className='form-check-input h-20px w-20px cursor-pointer'
                                                              checked={
                                                                selectedAccess[childrenData.id] ===
                                                                'read'
                                                              }
                                                              onChange={() =>
                                                                handleAccessChange(
                                                                  childrenData.id,
                                                                  selectedAccess[
                                                                    childrenData.id
                                                                  ] === 'read'
                                                                    ? ''
                                                                    : 'read',
                                                                  data.id,
                                                                  data.children || []
                                                                )
                                                              }
                                                            />
                                                            <p className='mb-0'>
                                                              {formatMessage({id: 'Read Only'})}
                                                            </p>
                                                          </span>

                                                          <span className='d-flex align-items-center gap-md-3 gap-2'>
                                                            <input
                                                              type='checkbox'
                                                              className='form-check-input h-20px w-20px cursor-pointer'
                                                              checked={
                                                                selectedAccess[childrenData.id] ===
                                                                'full'
                                                              }
                                                              onChange={() =>
                                                                handleAccessChange(
                                                                  childrenData.id,
                                                                  selectedAccess[
                                                                    childrenData.id
                                                                  ] === 'full'
                                                                    ? ''
                                                                    : 'full',
                                                                  data.id,
                                                                  data.children || []
                                                                )
                                                              }
                                                            />
                                                            <p className='mb-0'>
                                                              {formatMessage({id: 'Full Access'})}
                                                            </p>
                                                          </span>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )
                                                }
                                              )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                })}
                          </>
                        )}

                        {/* Step 4  */}
                        {currentDocumentStep === 4 && (
                          <>
                            <div className='w-100 my-4'>
                              <InfoCard2
                                title={formatMessage({
                                  id: 'Define a New Role for Users to Access RecordsKeeper.AI',
                                })}
                                desc={formatMessage({
                                  id: 'This wizard will guide you through defining a new role that can be assigned to users in your team, enabling them to access various modules of RecordsKeeper.AI. A role allows you to control what features and data a user can interact with. Once created, you can assign this role to multiple users as needed.',
                                })}
                                slug={`https://help.recordskeeper.ai/en/articles/10570867-how-to-add-team-members-and-configure-role-based-access-in-recordskeeper-ai`}
                                className='w-100 mb-0'
                              />
                            </div>
                            <div className='card border border-2 p-4 d-flex flex-column gap-5 mb-4'>
                              <div className='d-flex flex-md-row flex-column gap-4'>
                                {/* <div className='flex-fill'>
                                  <h1 className='font-size-16 mb-1'>
                                    {isLoading ? (
                                      <Skeleton
                                        height={16}
                                        width='70%'
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    ) : (
                                      // formikRef.current.values?.roleName || '-'
                                      ''
                                    )}
                                  </h1>
                                  <p className='text-muted mb-0'>
                                    {formatMessage({ id: 'Role Name' })}
                                  </p>
                                </div> */}
                                <div className='flex-fill'>
                                  <h1 className='font-size-16 mb-1'>
                                    {isLoading ? (
                                      <Skeleton
                                        height={16}
                                        width='70%'
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    ) : (
                                      formik.values?.roleName || '-'
                                    )}
                                  </h1>
                                  <p className='text-muted mb-0'>
                                    {formatMessage({id: 'Role Name'})}
                                  </p>
                                </div>

                                <div className='flex-fill'>
                                  <h1 className='font-size-16 mb-1'>
                                    {isLoading ? (
                                      <Skeleton
                                        height={16}
                                        width='70%'
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    ) : (
                                      `${currentUser?.firstName} ${currentUser?.lastName}`
                                    )}
                                  </h1>
                                  <p className='text-muted mb-0'>
                                    {formatMessage({id: 'Created by'})}
                                  </p>
                                </div>
                                <div className='flex-fill'>
                                  <h1 className='font-size-16 mb-1'>
                                    {isLoading ? (
                                      <Skeleton
                                        height={16}
                                        width='70%'
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    ) : (
                                      formatMessage({
                                        id: DateTime.now().toFormat('dd MMM yy, h:mm a'),
                                      })
                                    )}
                                  </h1>
                                  <p className='text-muted mb-0'>
                                    {formatMessage({id: 'Created On'})}
                                  </p>
                                </div>
                                <div className='flex-fill'>
                                  <h1 className='font-size-16 mb-1'>
                                    {isLoading ? (
                                      <Skeleton
                                        height={16}
                                        width='70%'
                                        baseColor='var(--kt-skeleton-bg-color)'
                                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                      />
                                    ) : (
                                      formatMessage({id: '-'})
                                    )}
                                  </h1>
                                  <p className='text-muted mb-0'>
                                    {formatMessage({id: 'Last updated on'})}
                                  </p>
                                </div>
                              </div>
                              {/* <p className='mb-0'> */}
                              {/* {isLoading ? (
                                <Skeleton
                                  height={60}
                                  width='100%'
                                  baseColor='var(--kt-skeleton-bg-color)'
                                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                />
                              ) : (
                                // formikRef.current.values?.roleDescription
                                ''
                              )} */}
                              {/* </p> */}
                            </div>
                            <div>
                              {previewRolePriviledge &&
                              previewRolePriviledge.find(
                                (data: any) => data.id === 'recordskeeper-settings'
                              )?.resources.length === 0 ? ( // Find only recordskeeper-settings
                                <div className='d-flex justify-content-between align-items-center py-1'>
                                  <p className='fw-bold mb-2'>
                                    {
                                      previewRolePriviledge.find(
                                        (data: any) => data.id === 'recordskeeper-settings'
                                      )?.title
                                    }
                                  </p>
                                  <div className='d-flex gap-2'>
                                    {previewRolePriviledge
                                      .find((data: any) => data.id === 'recordskeeper-settings')
                                      ?.operations.map((operation: any, index: number) => (
                                        <span
                                          key={index}
                                          className={`p-1 bg-${
                                            operation === 'full'
                                              ? 'success'
                                              : operation === 'read'
                                              ? 'muted'
                                              : operation === 'update'
                                              ? 'warning'
                                              : operation === 'create'
                                              ? 'primary'
                                              : 'danger'
                                          } bg-opacity-10 text-${
                                            operation === 'full'
                                              ? 'success'
                                              : operation === 'read'
                                              ? 'muted'
                                              : operation === 'update'
                                              ? 'warning'
                                              : operation === 'create'
                                              ? 'primary'
                                              : 'danger'
                                          } rounded`}
                                        >
                                          {formatMessage({id: operation})}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              ) : (
                                <div className='d-flex flex-column gap-1 py-1 mb-4'>
                                  <p className='fw-bold mb-2'>
                                    {
                                      previewRolePriviledge.find(
                                        (data: any) => data.id === 'recordskeeper-settings'
                                      )?.title
                                    }
                                  </p>
                                  {previewRolePriviledge
                                    .find((data: any) => data.id === 'recordskeeper-settings')
                                    ?.resources.map((subtitle: any, index: number) => (
                                      <div
                                        key={subtitle.id}
                                        className={`d-flex justify-content-between align-items-center px-2 py-2 rounded ${
                                          index % 2 === 0 && 'bg-primary bg-opacity-10'
                                        }`}
                                      >
                                        <p className='mb-0'>{subtitle.title}</p>
                                        <div className='d-flex gap-2'>
                                          {subtitle.operations.includes('full') ? (
                                            <span className='p-1 bg-success bg-opacity-10 text-success rounded'>
                                              {formatMessage({id: 'Full Access'})}
                                            </span>
                                          ) : (
                                            subtitle.operations.map((op: string, idx: number) => (
                                              <span
                                                key={idx}
                                                className={`p-1 bg-${
                                                  op === 'read'
                                                    ? 'muted'
                                                    : op === 'update'
                                                    ? 'warning'
                                                    : op === 'create'
                                                    ? 'primary'
                                                    : 'danger'
                                                } bg-opacity-10 text-${
                                                  op === 'read'
                                                    ? 'muted'
                                                    : op === 'update'
                                                    ? 'warning'
                                                    : op === 'create'
                                                    ? 'primary'
                                                    : 'danger'
                                                } rounded`}
                                              >
                                                {formatMessage({id: op})}
                                              </span>
                                            ))
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>

                            <div className='d-flex flex-md-row flex-column gap-4'>
                              <div className='w-md-50 w-100'>
                                <h1 className='font-size-13 mb-4'>
                                  {formatMessage({id: 'Role Privileges'})}
                                </h1>
                                <div className='card border border-2 p-4'>
                                  {isLoading
                                    ? Array(9)
                                        .fill(null)
                                        .map((_, index) => (
                                          <Skeleton
                                            height={30}
                                            width='100%'
                                            baseColor='var(--kt-skeleton-bg-color)'
                                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                          />
                                        ))
                                    : previewRolePriviledge &&
                                      previewRolePriviledge
                                        .filter((data: any) => data.id !== 'recordskeeper-settings')
                                        .map((data: any, index: any) => {
                                          return (
                                            <div key={index}>
                                              {data.resources.length === 0 ? (
                                                <div className='d-flex justify-content-between align-items-center py-1'>
                                                  <p className='fw-bold mb-2'>{data.title}</p>
                                                  <div className='d-flex gap-2'>
                                                    {data.operations.map(
                                                      (operation: any, index: any) => {
                                                        return (
                                                          <span
                                                            key={index}
                                                            className={`p-1 bg-${
                                                              operation === 'full'
                                                                ? 'success'
                                                                : operation === 'read'
                                                                ? 'muted'
                                                                : operation === 'update'
                                                                ? 'warning'
                                                                : operation === 'create'
                                                                ? 'primary'
                                                                : 'danger'
                                                            } bg-opacity-10 text-${
                                                              operation === 'full'
                                                                ? 'success'
                                                                : operation === 'read'
                                                                ? 'muted'
                                                                : operation === 'update'
                                                                ? 'warning'
                                                                : operation === 'create'
                                                                ? 'primary'
                                                                : 'danger'
                                                            } rounded`}
                                                          >
                                                            {formatMessage({id: operation})}
                                                          </span>
                                                        )
                                                      }
                                                    )}
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className='d-flex flex-column gap-1 py-1'>
                                                  <p className='fw-bold mb-2'>{data?.title}</p>
                                                  {data.resources.map(
                                                    (subtitle: any, index: any) => {
                                                      return (
                                                        <div
                                                          className={`d-flex justify-content-between align-items-center px-2 py-2 rounded ${
                                                            index % 2 === 0 &&
                                                            'bg-primary bg-opacity-10'
                                                          }`}
                                                        >
                                                          <p className='mb-0'>{subtitle.title}</p>
                                                          <div className='d-flex gap-2'>
                                                            {subtitle.operations.includes(
                                                              'full'
                                                            ) ? (
                                                              <span className='p-1 bg-success bg-opacity-10 text-success rounded'>
                                                                {formatMessage({id: 'Full Access'})}
                                                              </span>
                                                            ) : (
                                                              subtitle.operations.map(
                                                                (op: string, idx: number) => (
                                                                  <span
                                                                    key={idx}
                                                                    className={`p-1 bg-${
                                                                      op === 'read'
                                                                        ? 'muted'
                                                                        : op === 'update'
                                                                        ? 'warning'
                                                                        : op === 'create'
                                                                        ? 'primary'
                                                                        : 'danger'
                                                                    } bg-opacity-10 text-${
                                                                      op === 'read'
                                                                        ? 'muted'
                                                                        : op === 'update'
                                                                        ? 'warning'
                                                                        : op === 'create'
                                                                        ? 'primary'
                                                                        : 'danger'
                                                                    } rounded`}
                                                                  >
                                                                    {formatMessage({id: op})}
                                                                  </span>
                                                                )
                                                              )
                                                            )}
                                                          </div>
                                                        </div>
                                                      )
                                                    }
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )
                                        })}
                                </div>
                              </div>
                              <div className='w-md-50 w-100'>
                                <h1 className='font-size-13 mb-4'>
                                  {formatMessage({id: 'Role Privileges for Recordskeeper.AI'})}
                                </h1>
                                <div className='card border border-2 p-4'>
                                  {isLoading
                                    ? Array(9)
                                        .fill(null)
                                        .map((_, index) => (
                                          <Skeleton
                                            height={30}
                                            width='100%'
                                            baseColor='var(--kt-skeleton-bg-color)'
                                            highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                          />
                                        ))
                                    : folderStructure &&
                                      folderStructure
                                        .filter((data: any) => checkedItems[data.id]) // Filter parent folders
                                        .map((data: any, index: number) => {
                                          const isExpanded = expandedFolders[data.id] || false

                                          return (
                                            <div className='d-flex flex-column' key={data.id}>
                                              <div className='d-flex flex-column justify-content-center'>
                                                <div className='bg-white p-4 border border-2 rounded d-flex flex-column gap-4 my-2'>
                                                  <div
                                                    className='d-flex flex-md-row flex-column justify-content-between align-items-center gap-4 cursor-pointer'
                                                    onClick={() => toggleExpandForRK(data.id)}
                                                  >
                                                    <div className='d-flex justify-content-start gap-4 align-items-center w-100'>
                                                      <i
                                                        className={`fa-solid fa-angle-${
                                                          isExpanded ? 'down' : 'right'
                                                        } cursor-pointer p-2 pe-0`}
                                                      ></i>

                                                      <img src={folderIcon} alt='folder-icon' />
                                                      <p className='mb-0'>
                                                        {formatMessage({id: data.name})}
                                                      </p>
                                                    </div>
                                                  </div>
                                                  <div className={`${!isExpanded && 'd-none'}`}>
                                                    {isExpanded &&
                                                      data.children &&
                                                      data.children
                                                        .filter(
                                                          (child: any) => checkedItems[child.id]
                                                        )
                                                        .map(
                                                          (childrenData: any, catIndex: number) => {
                                                            const childAccessLevel =
                                                              selectedAccess[childrenData.id] // Get child's selected access level

                                                            return (
                                                              <div
                                                                key={childrenData.id}
                                                                className={`d-flex flex-md-row flex-column align-items-center justify-content-between py-2 gap-4 department-adjuster py-4 ${
                                                                  catIndex % 2
                                                                    ? ''
                                                                    : 'bg-primary bg-opacity-10 rounded'
                                                                }`}
                                                              >
                                                                <div className='d-flex align-items-center gap-4 w-md-75 w-100 '>
                                                                  <img
                                                                    src={folderIcon}
                                                                    alt='folder-icon'
                                                                  />
                                                                  <p className='mb-0'>
                                                                    {formatMessage({
                                                                      id: childrenData.name,
                                                                    })}
                                                                  </p>
                                                                </div>
                                                                <div className='w-md-25 w-100'>
                                                                  <div className='d-flex justify-content-md-end justify-content-center gap-8'>
                                                                    <p className='mb-0 fw-bold'>
                                                                      {formatMessage({
                                                                        id:
                                                                          childAccessLevel ===
                                                                          'full'
                                                                            ? 'Full Access'
                                                                            : 'Read Only',
                                                                      })}
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            )
                                                          }
                                                        )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )
                                        })}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer className='container px-0'>
                  <div className='d-flex flex-md-row flex-column justify-content-md-end justify-content-center gap-3 w-100'>
                    {currentDocumentStep > 1 ? (
                      <div className='order-md-1 order-2'>
                        <button
                          type='button'
                          className='btn bg-light custom-button-property text-muted w-100'
                          onClick={prevStep}
                        >
                          {formatMessage({id: 'Back'})}
                        </button>
                      </div>
                    ) : (
                      <div className='order-md-1 order-2'>
                        <button
                          type='button'
                          className='btn bg-light custom-button-property text-muted w-100'
                          onClick={() => {
                            setShowModal(false)
                            formik.resetForm()
                            resetData()
                          }}
                        >
                          {formatMessage({id: 'Cancel'})}
                        </button>
                      </div>
                    )}
                    <div className='order-md-2 order-1'>
                      {currentDocumentStep < 4 ? (
                        <button
                          type='button'
                          className='btn bg-primary text-white custom-button-property w-100'
                          disabled={
                            (currentDocumentStep === 1 && (!formik.values.roleName || !formik.values.roleDescription || !formik.isValid)) ||
                            (currentDocumentStep === 3 &&
                              transformPermissions(filterCheckedOperations(checkedOperations))
                                .length === 0 &&
                              generateRKData(folderStructure).length === 0)
                          }
                          onClick={nextStep}
                        >
                          {formatMessage({id: 'Next'})}
                        </button>
                      ) : (
                        <button
                          type='submit'
                          className='btn bg-primary custom-button-property text-white w-100 '
                          disabled={!formik.isValid || isLoading}
                          onClick={() => createRoles(formik.values)}
                        >
                          {!isLoading && (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({id: roleId ? 'Update' : 'Submit'})}
                            </span>
                          )}
                          {isLoading && (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({id: 'Please wait...'})}
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </Modal.Footer>
              </Modal>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default AddTeamRoleModal
